import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { hasHigherProductLevelInProductInventorySelector, hasProductLineInBundledProductInventorySelector, hasProductLineSelectorInProductInventorySelector, hasProductVersionInBundledProductInventorySelector, hasProductVersionInProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { calculatedPriceForProductConfigurationsInListSelector, calculatedPriceSelector, planAdditionalCostSelector } from '../../../selectors/PricingSelectors';
import { HighlightedPriceDisplay } from '../../common/pricing/HighlightedPriceDisplay';
import PriceDisplay from '../../common/pricing/PriceDisplay';
import { useAppSelector } from '../../../reducers';
import { orderedProductLevels, STARTER, PROFESSIONAL, ENTERPRISE } from 'self-service-api/constants/ProductLevels';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { coreSeatSkuForProductLevelSelector, productForSkuIdSelector } from '../../../selectors/ProductsSelectors';
import { includedCoreSeatsQuantityForProductVersionSelector, isQuantifiableHubSelector } from '../../../selectors/ProductsSelectors';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { productLevelForCoreSeatsSelector } from '../../../selectors/ProductConfigsSelectors';
const StyledMessage = styled(FormattedMessage).withConfig({
  displayName: "HighlightedMerchPriceDisplay__StyledMessage",
  componentId: "sc-1g3xm42-0"
})(["font-weight:600;display:block;"]);

const HighlightedMerchPriceDisplay = (_ref) => {
  let {
    skuId,
    quantityPackSkuId,
    quantityPackQuantity,
    queryParamTerm,
    style = {}
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["skuId", "quantityPackSkuId", "quantityPackQuantity", "queryParamTerm", "style"]);

  const productConfigurations = [createProductConfig({
    skuId
  })];
  if (quantityPackSkuId && quantityPackQuantity && quantityPackQuantity > 0) productConfigurations.push(createProductConfig({
    skuId: quantityPackSkuId,
    quantity: quantityPackQuantity
  }));
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const showingOnlyAdditionalCost = useSelector(state => {
    return !isUngatedForSeats && hasProductVersionInProductInventorySelector(state, {
      skuId
    });
  });
  const pricingInfo = useSelector(state => showingOnlyAdditionalCost ? planAdditionalCostSelector(state, {
    productConfigurations,
    queryParamTerm
  }) : calculatedPriceSelector(state, {
    productConfigurations,
    queryParamTerm
  }));
  const includedSeats = useAppSelector(state => {
    return includedCoreSeatsQuantityForProductVersionSelector(state, {
      productVersionSkuId: skuId
    });
  });
  const {
    apiName,
    productLine,
    productLevel
  } = useSelector(state => {
    return productForSkuIdSelector(state, {
      skuId
    });
  });
  const ownsProductLineViaProducts = useAppSelector(state => {
    return hasProductLineSelectorInProductInventorySelector(state, {
      productLine
    });
  });
  const ownsProductLineViaBundle = useAppSelector(state => {
    return hasProductLineInBundledProductInventorySelector(state, {
      productLine
    });
  });
  const ownsProductLine = ownsProductLineViaProducts || ownsProductLineViaBundle;
  const ownsProductVersion = useAppSelector(state => hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const ownsProductViaBundle = useAppSelector(state => hasProductVersionInBundledProductInventorySelector(state, {
    skuId
  }));
  const ownsProduct = ownsProductVersion || ownsProductViaBundle;
  const isQuantifiableHub = useAppSelector(state => isQuantifiableHubSelector(state, {
    skuId
  }));
  const currencyCode = useAppSelector(activeCurrencySelector);
  const coreSeatTierOwned = useAppSelector(state => productLevelForCoreSeatsSelector(state, {
    productConfigurations: []
  }));
  const additionalSeatsPrice = useAppSelector(state => {
    if (!isUngatedForSeats) return 0; // core seat product level owned or merch card's product level, whichever higher

    const productLevelForCoreSeats = productLevelForCoreSeatsSelector(state, {
      productConfigurations
    });
    const coreSeatSku = coreSeatSkuForProductLevelSelector(state, {
      productLevel: productLevelForCoreSeats
    });
    if (!coreSeatSku) return 0;
    return calculatedPriceForProductConfigurationsInListSelector(state, {
      allProductConfigurations: productConfigurations,
      productConfigurations: [createProductConfig({
        skuId: coreSeatSku.skuId
      })],
      queryParamTerm
    }).monthly.calculatedPrice;
  }); // Show "No additional cost $0/mo" on Non-quantifiable hubs' pro+ cards that users own

  const showNoAdditionalCost = isUngatedForSeats && !isQuantifiableHub && ownsProduct && (productLevel === PROFESSIONAL || productLevel === ENTERPRISE); // If Pro customer, looking at a hub NOT OWNED, do not show "Starts at" on Starter
  // If Ent customer, looking at a hub NOT OWNED, do not show "Starts at" on Starter and Pro

  const removeStartsAtSubtext = isUngatedForSeats && !isQuantifiableHub && !ownsProductLine && orderedProductLevels.indexOf(coreSeatTierOwned) > orderedProductLevels.indexOf(productLevel);
  const showPricePerMonthPerSeat = isUngatedForSeats && (isQuantifiableHub || !isQuantifiableHub && ( // If the highest owned tier is free or starter, show the Starter price in "/mo/seat" format on ALL hubs
  coreSeatTierOwned === STARTER && productLevel === STARTER || // If looking at merch card of a hub OWNED, show Starter price in "/mo/seat" format
  ownsProductLine && productLevel === STARTER));
  const showIncludedSeats = isUngatedForSeats && !isQuantifiableHub && ( // Show "Included Y seats" on ALL Non-quantifiable hubs' Pro/Ent card
  productLevel === PROFESSIONAL || productLevel === ENTERPRISE || // Show "Included Y seats" on Starter cards only if they DO NOT OWN HUB [and NOT in "/mo/seat" format]
  productLevel === STARTER && !ownsProductLine && !showPricePerMonthPerSeat);
  const isDowngrade = useAppSelector(state => {
    return hasHigherProductLevelInProductInventorySelector(state, {
      productLine,
      productLevel
    });
  }); // Show "Additional Seats start at $X/mo" for all non-quantifiable hubs' cards that are NOT downgrades and NOT in the "/mo/seat" format

  const showAdditionalSeatsPriceSubtext = isUngatedForSeats && additionalSeatsPrice > 0 && !isQuantifiableHub && !isDowngrade && !showPricePerMonthPerSeat;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [removeStartsAtSubtext ? /*#__PURE__*/_jsx("div", {
      style: {
        // Aligns with other cards that has subtext in price display
        marginTop: '18px'
      },
      children: /*#__PURE__*/_jsx(HighlightedPriceDisplay, Object.assign({
        pricingInfo: pricingInfo,
        skuId: skuId,
        wrapperStyle: style,
        showingOnlyAdditionalCost: showingOnlyAdditionalCost,
        showNoAdditionalCost: showNoAdditionalCost,
        showPricePerMonthPerSeat: showPricePerMonthPerSeat,
        showNoSubtext: removeStartsAtSubtext
      }, rest))
    }) : /*#__PURE__*/_jsx(HighlightedPriceDisplay, Object.assign({
      pricingInfo: pricingInfo,
      skuId: skuId,
      wrapperStyle: style,
      showingOnlyAdditionalCost: showingOnlyAdditionalCost,
      showNoAdditionalCost: showNoAdditionalCost,
      showPricePerMonthPerSeat: showPricePerMonthPerSeat,
      showNoSubtext: removeStartsAtSubtext
    }, rest)), /*#__PURE__*/_jsxs("div", {
      className: "m-top-6",
      children: [showIncludedSeats ? /*#__PURE__*/_jsx(StyledMessage, {
        message: `merchCards.pricing.includes.seats.${productLevel}`,
        options: {
          count: includedSeats
        }
      }) : null, showAdditionalSeatsPriceSubtext ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "merchCards.pricing.subtexts.additionalSeatsStartAt_jsx",
        options: {
          seleniumId: apiName,
          currencyCode,
          additionalSeatsPrice
        },
        elements: {
          PriceDisplay
        }
      }) : null]
    })]
  });
};

export default HighlightedMerchPriceDisplay;